import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import {
  ChevronLeft,
  ChevronRight,
  Contrast,
  RotateCcw,
  SunMedium,
} from "lucide-react";

import { buildSeries } from "@/lib/imageStack";
import {
  useCaseImages,
  useLibraryCaseImages,
  type CaseImage,
} from "@/api/tests-api";

/**
 * Where the images come from.
 *
 * A candidate reads them through their own attempt, which is what scopes
 * the route to the case on THEIR sealed paper. An admin previewing the
 * library has no attempt, and reads the library route instead.
 *
 * Stated as a discriminated union rather than two optional ids, so a
 * caller cannot pass half of each.
 */
export type CaseImageSource =
  | { kind: "attempt"; attemptId: string; assignmentCaseId: string }
  | { kind: "library"; caseId: string };

export interface SeriesRailState {
  /** One entry per series, in reading order. */
  series: { key: string; label: string; count: number; seen: boolean }[];
  index: number;
  /** Series the reader has opened. Unopened ones are MARKED, not merely
   *  un-highlighted -- see the rail's own note. */
  unseen: number;
  select: (i: number) => void;
}

interface TestCaseViewerProps {
  source: CaseImageSource;
  /**
   * Render the series rail INSIDE the viewer, as its own left column.
   *
   * True for the admin preview and the reviewer's case viewer, which
   * have nowhere else to put it. FALSE for the candidate's sitting,
   * where the approved layout gives series a pane of their own beside
   * the case list -- there the state is lifted out through `onSeries`
   * and the rail is drawn by the page.
   *
   * The state is reported rather than duplicated: two lists deciding
   * separately which series is open is two lists that disagree.
   */
  showSeriesRail?: boolean;
  onSeries?: (state: SeriesRailState) => void;
}

/**
 * The images, and nothing else.
 *
 * A separate component from the clinical `StudyImagesModal` rather than a
 * parameterised version of it, for two reasons. That component's
 * filmstrip *is* the report-image curator — it writes
 * `data_json._report_images` through an editor prop, and it decides which
 * images print on a signed clinical report. A candidate curates nothing,
 * and coupling an exam feature to that surface puts every clinical
 * regression in its blast radius.
 *
 * The second reason is structural: CT and MR are multi-series, and the
 * clinical viewer has no series concept at all because ultrasound does
 * not need one.
 *
 * Brightness and contrast are CSS filters. They are *display* controls,
 * not DICOM window/level — the pixels arrived as JPEG with a window
 * already baked in server-side. Real re-windowing would need the original
 * DICOM in the browser.
 */
export function TestCaseViewer({
  source,
  showSeriesRail = true,
  onSeries,
}: TestCaseViewerProps) {
  // Both hooks always run -- hooks cannot be conditional -- and each is
  // disabled unless it is the one in use.
  const attemptImages = useCaseImages(
    source.kind === "attempt" ? source.attemptId : "",
    source.kind === "attempt" ? source.assignmentCaseId : null,
  );
  const libraryImages = useLibraryCaseImages(
    source.kind === "library" ? source.caseId : null,
  );
  const images = source.kind === "attempt" ? attemptImages : libraryImages;
  // Identifies the case regardless of which route it came through, so the
  // "new case, reset the view" effect works for both.
  const caseKey =
    source.kind === "attempt" ? source.assignmentCaseId : source.caseId;
  const [seriesIndex, setSeriesIndex] = useState(0);
  const [frameIndex, setFrameIndex] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [inverted, setInverted] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const wheelAcc = useRef(0);
  // Which series this candidate has actually LOOKED AT, for this case.
  //
  // A case can carry several series and the rail is easy to miss, so a
  // candidate can dictate a full report having seen one of three -- and
  // nothing on the screen tells them, or the reviewer, that the other
  // two were never opened. This marks the ones still unseen and warns
  // before they record. Reset per case, deliberately: "seen" means seen
  // in this sitting, on this case.
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0]));

  const series = useMemo(
    () => buildSeries(images.data ?? []),
    [images.data],
  );
  const current = series[seriesIndex];
  const unseen = series.length - seen.size;
  const frames: CaseImage[] = current?.images ?? [];
  const frame = frames[Math.min(frameIndex, Math.max(0, frames.length - 1))];
  const atLastFrame = frames.length > 0 && frameIndex >= frames.length - 1;
  // The next series worth offering: an UNOPENED one first, wherever it
  // sits, then simply the following one. Null when there is nothing to
  // offer, so a single-series case shows nothing at all.
  const nextSeries = useMemo(() => {
    if (series.length < 2) return null;
    const unopened = series.findIndex((_s, i) => !seen.has(i));
    if (unopened !== -1) return unopened;
    return seriesIndex < series.length - 1 ? seriesIndex + 1 : null;
  }, [series, seen, seriesIndex]);

  // REPORT the rail rather than let the page rebuild it. `buildSeries`
  // runs here already, and a second derivation in the sitting would be a
  // second answer to "which series is this" the day either changes.
  useEffect(() => {
    onSeries?.({
      series: series.map((s, i) => ({
        key: s.key,
        label: s.label,
        count: s.images.length,
        seen: seen.has(i),
      })),
      index: seriesIndex,
      unseen,
      select: setSeriesIndex,
    });
  }, [series, seriesIndex, seen, unseen, onSeries]);

  // Reset position when the case or series changes, so a new case does
  // not open halfway down the previous one's stack.
  useEffect(() => {
    setSeriesIndex(0);
    setFrameIndex(0);
    setSeen(new Set([0]));
  }, [caseKey]);
  useEffect(() => {
    setFrameIndex(0);
    setSeen((prev) =>
      prev.has(seriesIndex) ? prev : new Set(prev).add(seriesIndex),
    );
  }, [seriesIndex]);

  const step = useCallback(
    (delta: number) => {
      setFrameIndex((i) => {
        const next = i + delta;
        if (next < 0) return 0;
        if (next > frames.length - 1) return Math.max(0, frames.length - 1);
        return next;
      });
    },
    [frames.length],
  );

  // PACS-style stack scroll: one image per wheel notch.
  //
  // Registered natively with { passive: false } because React's onWheel
  // is passive and cannot preventDefault, which would let the page scroll
  // underneath the viewer. The accumulator normalises deltaMode (lines vs
  // pixels vs pages) and resets rather than carrying credit, so a
  // trackpad flick does not fly through thirty slices.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
      wheelAcc.current += e.deltaY * unit;
      const threshold = 40;
      if (Math.abs(wheelAcc.current) >= threshold) {
        step(wheelAcc.current > 0 ? 1 : -1);
        wheelAcc.current = 0;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [step]);

  // Arrow keys move through the stack too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  const filter = `brightness(${brightness}%) contrast(${contrast}%)${
    inverted ? " invert(1)" : ""
  }`;

  if (images.isLoading) {
    return (
      <div
        data-cy="case-images-loading"
        className="flex h-full items-center justify-center text-sm text-foreground-muted"
      >
        Loading images…
      </div>
    );
  }

  if ((images.data?.length ?? 0) === 0) {
    return (
      <div
        data-cy="case-images-empty"
        className="flex h-full items-center justify-center rounded-lg border border-dashed border-border p-10 text-sm text-foreground-muted"
      >
        This case has no images.
      </div>
    );
  }

  return (
    /* A ROW, NOT A COLUMN, AND THE RAIL IS THE LEFT PANE.
       It was a strip of tabs above the image. With thirteen series that
       wrapped to four rows and ate the height the stack is read in --
       and height is the scarce dimension: a screen is wider than it is
       tall, and a CT slice is square. Down the left it costs 210px of
       width, which there is plenty of, and returns every pixel of height
       to the image. It is also where every PACS puts it. */
    <div className="flex min-h-0 flex-1 gap-3" data-cy="test-case-viewer">
      {/* Series rail — only when there is a choice to make, and only
          when this viewer is the one drawing it. */}
      {showSeriesRail && series.length > 1 && (
        <div
          data-cy="series-rail"
          className="flex w-[210px] shrink-0 flex-col border-r border-border pr-3"
        >
          {/* SAY THERE ARE SEVERAL. The rail used to be a row of small
              tabs that read as decoration, and a candidate could dictate
              a whole report having seen one series of three without ever
              noticing the others. Naming the count is the cheapest half
              of the fix; the unseen markers below are the other half. */}
          <div className="mb-1.5 flex shrink-0 flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
              {series.length} series
            </span>
            {unseen > 0 && (
              <span
                data-cy="series-unseen-warning"
                className="rounded-full bg-status-wip-bg px-2 py-0.5 text-[11px] font-semibold text-status-wip"
              >
                {unseen} not opened yet
              </span>
            )}
          </div>
          {/* THE LIST SCROLLS, IT DOES NOT WRAP. A study with thirty
              series must not push the stage out of the panel. */}
          <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
            {series.map((s, i) => {
              const isSeen = seen.has(i);
              return (
                <button
                  key={s.key}
                  type="button"
                  data-cy="series-tab"
                  data-seen={isSeen ? "true" : "false"}
                  onClick={() => setSeriesIndex(i)}
                  title={isSeen ? undefined : "You have not opened this series"}
                  className={[
                    "flex w-full shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition-colors",
                    i === seriesIndex
                      ? "bg-accent text-accent-text"
                      : isSeen
                        ? "bg-background-card text-foreground-muted hover:text-foreground"
                        : // Unopened: marked, not merely un-highlighted.
                          "border border-status-wip bg-status-wip-bg text-status-wip hover:brightness-110",
                  ].join(" ")}
                >
                  {!isSeen && i !== seriesIndex && (
                    <span aria-hidden className="shrink-0 text-[13px] leading-none">
                      &bull;
                    </span>
                  )}
                  {/* The name truncates rather than wrapping: a rail whose
                      rows are three lines tall is a rail you scroll. The
                      full name is on the element for a hover. */}
                  <span className="min-w-0 flex-1 truncate" title={s.label}>
                    {s.label}
                  </span>
                  <span className="shrink-0 tabular-nums opacity-70">
                    {s.images.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* The image and everything that acts on it. min-w-0 so a wide
          filmstrip cannot push the column past the viewer's width. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Controls. A RULED BAR at the top of the card rather than a row
          of loose chrome above a floating stage -- the design makes the
          toolbar, the image and the filmstrip one object. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-3.5 py-2.5 text-xs text-foreground-muted">
        <span data-cy="frame-position" className="font-mono tabular-nums">
          {frames.length ? frameIndex + 1 : 0} / {frames.length}
        </span>
        <button
          type="button"
          aria-label="Previous image"
          onClick={() => step(-1)}
          className="rounded border border-border p-1 hover:border-accent"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label="Next image"
          onClick={() => step(1)}
          className="rounded border border-border p-1 hover:border-accent"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>

        <label className="flex items-center gap-1">
          <SunMedium className="h-3.5 w-3.5" aria-hidden />
          <input
            type="range"
            min={40}
            max={200}
            value={brightness}
            aria-label="Brightness"
            onChange={(e) => setBrightness(Number(e.target.value))}
            className="w-20 accent-accent"
          />
        </label>
        <label className="flex items-center gap-1">
          <Contrast className="h-3.5 w-3.5" aria-hidden />
          <input
            type="range"
            min={40}
            max={220}
            value={contrast}
            aria-label="Contrast"
            onChange={(e) => setContrast(Number(e.target.value))}
            className="w-20 accent-accent"
          />
        </label>
        <button
          type="button"
          data-cy="btn-invert"
          onClick={() => setInverted((v) => !v)}
          aria-pressed={inverted}
          className={[
            "rounded border px-2 py-0.5",
            inverted ? "border-accent text-accent" : "border-border",
          ].join(" ")}
        >
          Invert
        </button>
        <button
          type="button"
          aria-label="Reset display"
          onClick={() => {
            setBrightness(100);
            setContrast(100);
            setInverted(false);
          }}
          className="rounded border border-border p-1 hover:border-accent"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Stage */}
      <div
        ref={stageRef}
        data-cy="viewer-stage"
        className="relative min-h-[300px] w-full flex-1 overflow-hidden bg-[#0B0C0D]"
      >
        <TransformWrapper
          // The wheel drives stack scroll, not zoom — that is what a
          // radiologist's hand expects. Zoom is ctrl+wheel / pinch /
          // double-click.
          wheel={{ disabled: true }}
          doubleClick={{ mode: "toggle" }}
          minScale={1}
          maxScale={8}
        >
          <TransformComponent
            wrapperClass="!h-full !w-full"
            contentClass="!h-full !w-full flex items-center justify-center"
          >
            {frame && (
              <img
                data-cy="viewer-image"
                src={frame.url}
                alt={`Image ${frameIndex + 1} of ${frames.length}`}
                style={{ filter }}
                className="max-h-full max-w-full object-contain"
                draggable={false}
              />
            )}
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* Filmstrip. Only when there is more than one frame to pick from —
          a single-image case gets a strip of one, which is just clutter.
          The thumbnails are the stored JPEGs themselves rather than a
          separate size: there is no thumbnail endpoint, and generating
          one would mean a second conversion pass over every instance at
          upload. `loading="lazy"` keeps a 300-slice stack from fetching
          all of them at once. */}
      {frames.length > 1 && (
        <div
          data-cy="viewer-filmstrip"
          className="flex gap-1.5 overflow-x-auto bg-[#17191B] px-3 py-2.5"
        >
          {frames.map((f, i) => (
            <button
              key={f.sop_instance_uid}
              type="button"
              data-cy="filmstrip-thumb"
              aria-label={`Image ${i + 1}`}
              aria-current={i === frameIndex}
              ref={(el) => {
                // Keep the selected thumbnail in view when the stack is
                // driven by the wheel rather than by clicking here.
                if (i === frameIndex && el) {
                  el.scrollIntoView({ block: "nearest", inline: "nearest" });
                }
              }}
              onClick={() => setFrameIndex(i)}
              className={[
                "relative h-14 w-14 shrink-0 overflow-hidden rounded border bg-black transition-colors",
                i === frameIndex
                  ? "border-accent ring-1 ring-accent"
                  : "border-border hover:border-accent/60",
              ].join(" ")}
            >
              <img
                src={f.url}
                alt=""
                loading="lazy"
                draggable={false}
                style={{ filter }}
                className="h-full w-full object-cover"
              />
              <span className="absolute bottom-0 right-0 bg-black/60 px-1 text-[9px] leading-tight text-white">
                {i + 1}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* The hint is the card's footer rule, on the subtle ground -- the
          design closes the object rather than leaving text under it. */}
      <p className="sk-sit-note border-t border-border bg-background-subtle px-3.5 py-2.5 text-foreground-muted">
        Scroll to move through the images. Hold Ctrl and scroll, or
        double-click, to zoom.
      </p>

      {/* END OF SERIES, offered rather than taken.
          The obvious fix for a missed rail is to let the wheel carry on
          into the next series, and that is the one thing not to do: in
          every PACS the wheel stacks WITHIN a series, and the hard stop
          at the end is information -- "this series is finished". Sliding
          silently into different anatomy while somebody is dictating is
          how a finding gets attributed to the wrong study, and it would
          not even fix the complaint: a candidate who never noticed the
          rail still would not know a second series existed, they would
          just see one longer stack.
          So the boundary stays, and it speaks. */}
      {atLastFrame && nextSeries !== null && (
        <button
          type="button"
          data-cy="btn-next-series"
          onClick={() => setSeriesIndex(nextSeries)}
          className="m-3 self-start rounded-lg border border-accent bg-accent-light px-3 py-1.5 text-xs font-semibold text-accent-hover"
        >
          End of {current?.label ?? "this series"} — open{" "}
          {series[nextSeries]?.label ?? "the next series"} &rarr;
        </button>
      )}
      </div>
    </div>
  );
}
